import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges} from '@angular/core';

import {Destroyable} from '../../../destroyable.component';
import {EmpContractAttributes} from '../../../models/models.interfaces';
import {Data} from '../../../shared/api';
import {SdcSyncStatus} from '../../../shared/employees/contract/contract.interfaces';
import {RolesService} from '../../authentication/roles/roles.service';
import {SdcStatusDTO} from '../sdconnect.model';
import {SdconnectService} from '../sdconnect.service';

interface SdcSyncStatusWidget {
  label: string;
  state: string;
  tooltip: string;
  cssClass: string;
  versionTag: string;
}

/**
 *  Component displays a DOM element for enabled tenants depending on synchronization status
 *  When field sentToPayroll is not set to true -> status is OFF
 *  When field sentToPayroll is set to true
 *    If there is blocking alerts -> status is set to blocked.
 *    If there is sync. errors    -> status is set to error.
 *    Else                        -> status is set to OK.
 *
 *  For SDC3, two independent sync flows are displayed: person master data and payroll input,
 *  each driven by its own status field on the contract.
 */
@Component({
  selector: 'gp-sdc-statuses',
  templateUrl: './sdc-statuses.component.html',
  styleUrls: ['./sdc-statuses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SdcStatusesComponent extends Destroyable implements OnChanges {

  @Input()
  public sdcStatus: SdcStatusDTO;
  @Input()
  public contract: Data<EmpContractAttributes>;

  public widgets: SdcSyncStatusWidget [] = [];

  constructor(private cd: ChangeDetectorRef,
              private rolesService: RolesService,
              private sdconnectService: SdconnectService) {
    super();
  }

  ngOnChanges(): void {
    if (!this.sdconnectService.isSdconnectEnabled()) {
      return;
    }
    this.processStatus(this.sdcStatus, this.contract);
  }

  private processStatus(sdcStatus: SdcStatusDTO, empContract: Data<EmpContractAttributes>): void {
    if (!sdcStatus) {
      return;
    }

    const hasSDC3AdminRole = this.rolesService.hasRole('SDC3_ADMIN');

    this.widgets = [];
    if (sdcStatus.personMasterDataSyncOnSDC2Activated) {
      this.widgets.push(this.buildWidget(
        'SDC',
        this.getSDC2SyncState(sdcStatus),
        hasSDC3AdminRole && sdcStatus.personMasterDataSyncOnSDC3Activated ? 'v2' : null)
      );

    }
    if (sdcStatus.personMasterDataSyncOnSDC3Activated && (!sdcStatus.personMasterDataSyncOnSDC2Activated || hasSDC3AdminRole)) {
      this.widgets.push(this.buildWidget(
        'SDC',
        sdcStatus.personMasterDataSyncEnabled === false ? 'off' : this.getSDC3SyncState(empContract, 'personMasterDataSyncStatus'),
        hasSDC3AdminRole && sdcStatus.personMasterDataSyncOnSDC2Activated ? 'v3' : null)
      );
    }

    if (sdcStatus.payrollInputSyncActivated && hasSDC3AdminRole) {
      this.widgets.push(this.buildWidget('SDC.PI', this.getSDC3SyncState(empContract, 'payrollInputSyncStatus')));
    }

    this.cd.markForCheck();
  }

  private buildWidget(keyPrefix: string, state: string, versionTag = null): SdcSyncStatusWidget {
    return {
      label: `${keyPrefix}.STATUS.service`,
      state: `SDC.STATUS.${state}`,
      cssClass: `sdc-${state}`,
      tooltip: `${keyPrefix}.STATUS.tooltip.${state}`,
      versionTag: versionTag
    };
  }

  private getSDC2SyncState(sdcStatus: SdcStatusDTO): string {
    if (sdcStatus.personMasterDataSyncEnabled === false) {
      return 'off';
    } else if (sdcStatus.alerts?.includes('SdConnectSync')) {
      return 'blocked';
    } else if (this.hasSDC2Error() && this.rolesService.hasRole('SDCONNECT_ADMIN')) {
      return 'error';
    } else if (sdcStatus.personMasterDataSyncEnabled !== null) {
      return 'ok';
    }
    return 'unknown';
  }

  private hasSDC2Error() {
    return this.sdcStatus?.errors?.filter(error => error !== 'SDC3').length > 0;
  }

  private getSDC3SyncState(contract: Data<EmpContractAttributes>,
                           syncType: 'personMasterDataSyncStatus' | 'payrollInputSyncStatus'): string {
    return (SdcSyncStatus[contract.attributes?.[syncType]] ?? 'unknown').toLowerCase();
  }
}
